import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import RefundForm from './forms/RefundForm';
import ExchangeForm from './forms/ExchangeForm';
import OperationSelector from './forms/OperationSelector';
import SaleInvoiceServices from '../../services/SaleInvoice';

const RefundExchangePage = () => {
  const { invoiceId, operation } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [preselectedInvoices, setPreselectedInvoices] = useState([]);
  const [preselectedItems, setPreselectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Fetch sale invoice data if invoiceId is provided
  useEffect(() => {
    if (invoiceId) {
      setLoading(true);
      SaleInvoiceServices.getSaleInvoiceById(invoiceId)
        .then(({ data }) => {
          const invoice = data[0] || data;
          setInvoiceData(invoice);
          setPreselectedInvoices([invoice]);
          setPreselectedItems(
            invoice.sale_invoice_items
              .filter(item => (item.quantity || 0) > 0)
              .map(item => ({ ...item, invoiceId: invoice.id }))
          );
        })
        .catch((error) => {
          console.error('Failed to fetch sale invoice:', error);
          // Navigate back to sales list if invoice not found
          navigate('/sales');
        })
        .finally(() => setLoading(false));
    }
  }, [invoiceId, navigate]);

  // Handle operation selection from location state (for backward compatibility)
  useEffect(() => {
    if (location.state?.preselectedInvoices) {
      setPreselectedInvoices(location.state.preselectedInvoices);
    }
    if (location.state?.preselectedItems) {
      setPreselectedItems(location.state.preselectedItems);
    }
  }, [location.state]);

  const handleSubmit = async (payload, res) => {
    // Navigate back to the sale invoice after successful submission
    if (invoiceId) {
      navigate(`/sales/invoice/${invoiceId}`);
    }
  };

  const handleClose = () => {
    // Navigate back to the sale invoice or sales list
    if (invoiceId) {
      navigate(`/sales/invoice/${invoiceId}`);
    } else {
      navigate('/sales');
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header d-flex align-items-center justify-content-between">
          <div className="page-title">
            <h4>Refund & Exchange</h4>
            {invoiceId && invoiceData && (
              <p className="mb-0" style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                Invoice #{invoiceData.order_number || invoiceId} - {invoiceData.billing_name} {invoiceData.billing_last_name}
              </p>
            )}
          </div>
          <div className="d-flex align-items-center">
            <button className="btn btn-cancel me-2" onClick={handleClose}>
              Back to Invoice
            </button>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/sales">Sales</Link>
              </li>
              {invoiceId && invoiceData && (
                <li className="breadcrumb-item">
                  <Link to={`/sales/invoice/${invoiceId}`}>
                    Invoice #{invoiceData.order_number || invoiceId}
                  </Link>
                </li>
              )}
              <li className="breadcrumb-item">
                <Link to={`/refund-exchange/${invoiceId}`}>
                  Refund & Exchange
                </Link>
              </li>
              {operation && (
                <li className="breadcrumb-item active" aria-current="page">
                  {operation === 'refund' ? 'Refund' : 'Exchange'}
                </li>
              )}
              {!operation && (
                <li className="breadcrumb-item active" aria-current="page">
                  Select Operation
                </li>
              )}
            </ol>
          </nav>
        </div>

        {loading ? (
          <div className="card">
            <div className="card-body text-center" style={{ padding: '48px' }}>
              <div className="whirly-loader"></div>
              <p className="mt-3">Loading invoice data...</p>
            </div>
          </div>
        ) : (
          <>
            {!operation && (
              <OperationSelector
                preselectedInvoices={preselectedInvoices}
                preselectedItems={preselectedItems}
                onClose={handleClose}
              />
            )}
            {operation === 'refund' && (
              <RefundForm
                preselectedInvoices={preselectedInvoices}
                preselectedItems={preselectedItems}
                onSubmit={handleSubmit}
                onClose={handleClose}
              />
            )}
            {operation === 'exchange' && (
              <ExchangeForm
                preselectedInvoices={preselectedInvoices}
                preselectedItems={preselectedItems}
                onSubmit={handleSubmit}
                onClose={handleClose}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RefundExchangePage; 